import { useState, useRef, useCallback, useEffect } from 'react'
import { API_HOST, goToLoginIfSessionClosed } from '../api/client'

interface VoiceTranscriptionState {
  isConnecting: boolean
  isRecording: boolean
  isProcessing: boolean
  currentText: string
  finalText: string
  error: string | null
  isTranscriptionReady: boolean
}

interface UseVoiceTranscriptionReturn {
  state: VoiceTranscriptionState
  startRecording: (initialText?: string) => void
  stopRecording: () => void
  clearText: () => void
  disconnect: () => void
}

export const useVoiceTranscription = (sessionId: string): UseVoiceTranscriptionReturn => {
  const [state, setState] = useState<VoiceTranscriptionState>({
    isConnecting: false,
    isRecording: false,
    isProcessing: false,
    currentText: '',
    finalText: '',
    error: null,
    isTranscriptionReady: false
  })

  const websocketRef = useRef<WebSocket | null>(null)
  const audioStreamRef = useRef<MediaStream | null>(null)
  const isConnectedRef = useRef(false)

  const connect = useCallback(() => {
    if (websocketRef.current?.readyState === WebSocket.OPEN || state.isConnecting) {
      console.log('Already connected or connecting')
      return
    }

    console.log('Connecting to voice transcription WebSocket...')
    setState(prev => ({ ...prev, isConnecting: true, error: null }))

    // Create WebSocket URL matching the pattern used in GenieChat
    const protocol = window.location.protocol === "https:" ? "wss" : "ws"
    const wsUrl = `${protocol}://${API_HOST}/ws/voice-transcription/${sessionId}`
    console.log('WebSocket URL:', wsUrl)
    
    const ws = new WebSocket(wsUrl)
    websocketRef.current = ws

    ws.onopen = () => {
      console.log('Voice transcription WebSocket connected successfully')
      setState(prev => ({ ...prev, isConnecting: false }))
      isConnectedRef.current = true
      
      // Set up a keep-alive interval
      const keepAliveInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'keepalive' }))
        } else {
          clearInterval(keepAliveInterval)
        }
      }, 5000) // Send keepalive every 5 seconds
      
      // Store interval ID for cleanup
      ;(ws as any)._keepAliveInterval = keepAliveInterval
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        handleWebSocketMessage(data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }

    ws.onclose = (event) => {
      console.log('Voice transcription WebSocket disconnected', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      })
      console.trace('WebSocket close stack trace')
      
      // Clear keep-alive interval
      const interval = (ws as any)._keepAliveInterval
      if (interval) {
        clearInterval(interval)
      }
      
      isConnectedRef.current = false
      setState(prev => ({ 
        ...prev, 
        isConnecting: false, 
        isRecording: false, 
        isProcessing: false,
        isTranscriptionReady: false,
        error: event.reason || 'Connection lost'
      }))
      goToLoginIfSessionClosed(event)
    }

    ws.onerror = (error) => {
      console.error('Voice transcription WebSocket error:', error)
      setState(prev => ({ 
        ...prev, 
        isConnecting: false, 
        isRecording: false,
        isProcessing: false,
        error: 'Connection error. Please try again.' 
      }))
    }
  }, [sessionId])

  const handleWebSocketMessage = useCallback((data: any) => {
    console.log('VOICE DEBUG: Received WebSocket message:', data)
    
    switch (data.type) {
      case 'transcription_ready':
        console.log('Transcription service ready')
        setState(prev => ({ ...prev, isTranscriptionReady: true }))
        break
        
      case 'ping':
        // Respond to server pings
        if (websocketRef.current?.readyState === WebSocket.OPEN) {
          websocketRef.current.send(JSON.stringify({ type: 'pong' }))
        }
        break
        
      case 'error':
        console.error('Server error:', data.message)
        setState(prev => ({ 
          ...prev, 
          error: data.message || 'Server error',
          isRecording: false,
          isProcessing: false
        }))
        break

      case 'transcript_update':
        console.log('VOICE DEBUG: transcript_update received:', data.text)
        setState(prev => ({ 
          ...prev, 
          finalText: prev.finalText + (prev.finalText ? ' ' : '') + (data.text || ''),
          currentText: '',
          isProcessing: false
        }))
        break

      case 'transcript_delta':
        console.log('VOICE DEBUG: transcript_delta received:', data.text)
        setState(prev => ({ 
          ...prev, 
          currentText: prev.currentText + (data.text || ''),  // Show live transcription
          isProcessing: true
        }))
        break

      case 'transcription_complete':
        console.log('VOICE DEBUG: transcription_complete received:', data.text)
        setState(prev => ({ 
          ...prev, 
          finalText: data.text || prev.currentText,  // Final transcript
          currentText: '',
          isProcessing: false 
        }))
        break

      case 'error':
        setState(prev => ({ 
          ...prev, 
          error: data.message || 'An error occurred during transcription',
          isRecording: false,
          isProcessing: false
        }))
        break
    }
  }, [])

  const startRecording = useCallback(async (initialText: string = '') => {
    console.log('startRecording called')
    
    // Set recording state immediately for instant UI feedback
    setState(prev => ({ 
      ...prev, 
      isRecording: true, 
      error: null,
      currentText: '',
      finalText: initialText  // Start with existing input text
    }))
    
    try {
      // Connect WebSocket if not connected
      if (!isConnectedRef.current) {
        console.log('WebSocket not connected, connecting...')
        connect()
        // Wait for connection
        await new Promise((resolve, reject) => {
          let timeout = 0
          const maxTimeout = 5000 // 5 seconds max wait
          const checkConnection = () => {
            if (isConnectedRef.current) {
              console.log('WebSocket connected, proceeding with recording')
              resolve(void 0)
            } else if (timeout >= maxTimeout) {
              console.error('WebSocket connection timeout')
              reject(new Error('WebSocket connection timeout'))
            } else {
              timeout += 100
              setTimeout(checkConnection, 100)
            }
          }
          checkConnection()
        })
      }

      // Request microphone access
      console.log('Requesting microphone access...')
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      })
      
      console.log('Microphone access granted')
      console.log('Audio tracks:', stream.getAudioTracks())
      audioStreamRef.current = stream
      
      // Verify the stream is active
      const audioTracks = stream.getAudioTracks()
      if (audioTracks.length === 0) {
        throw new Error('No audio tracks in stream')
      }
      if (!audioTracks[0].enabled) {
        console.warn('Audio track is disabled, enabling it')
        audioTracks[0].enabled = true
      }
      
      // Use Web Audio API to get raw PCM data directly
      const audioContext = new AudioContext({ sampleRate: 24000 })
      const source = audioContext.createMediaStreamSource(stream)
      const processor = audioContext.createScriptProcessor(4096, 1, 1)
      
      // Connect the audio pipeline
      source.connect(processor)
      processor.connect(audioContext.destination)
      
      // Handle audio processing
      processor.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer
        const inputData = inputBuffer.getChannelData(0) // Get mono channel
        
        // Convert float32 to int16 PCM
        const pcm16Buffer = new Int16Array(inputData.length)
        for (let i = 0; i < inputData.length; i++) {
          const sample = Math.max(-1, Math.min(1, inputData[i]))
          pcm16Buffer[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF
        }
        
        // Send to server
        if (ws && ws.readyState === WebSocket.OPEN) {
          const uint8Array = new Uint8Array(pcm16Buffer.buffer)
          const base64Audio = btoa(String.fromCharCode(...uint8Array))
          
          console.log('Sending PCM16 audio chunk, length:', pcm16Buffer.length, 'base64 length:', base64Audio.length)
          ws.send(JSON.stringify({
            type: 'audio_chunk',
            audio: base64Audio
          }))
        }
      }
      
      // Store references for cleanup
      const audioRefs = { audioContext, source, processor }
      ;(stream as any)._audioRefs = audioRefs
      
      // Capture websocket reference to avoid closure issues
      const ws = websocketRef.current
      
      // Start transcription on server first
      console.log('WebSocket ready state:', websocketRef.current?.readyState)
      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        console.log('Sending start_transcription message')
        websocketRef.current.send(JSON.stringify({
          type: 'start_transcription'
        }))
        
        // Wait for transcription_ready message
        console.log('Waiting for transcription ready...')
        setState(prev => ({ ...prev, isTranscriptionReady: false }))
        
        await new Promise((resolve, reject) => {
          let timeout = 0
          
          const checkReady = () => {
            // Check the websocket ref to see current state
            if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
              reject(new Error('WebSocket connection lost while waiting for transcription'))
              return
            }
            
            // For now, just wait 2 seconds for OpenAI to connect
            // TODO: Properly wait for isTranscriptionReady flag
            if (timeout >= 2000) {
              console.log('Proceeding after wait')
              resolve(void 0)
            } else {
              timeout += 100
              setTimeout(checkReady, 100)
            }
          }
          checkReady()
        })
        
        // Now start recording
        console.log('Starting MediaRecorder...')
        console.log('WebSocket state before start:', ws?.readyState)
        
        try {
          // Check if WebSocket is still open
          if (!ws || ws.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket closed before recording could start')
          }
          
          // Audio processing is now handled by the ScriptProcessor
          console.log('Audio processing started with Web Audio API')
          
        } catch (error) {
          console.error('Error starting audio processing:', error)
          throw error
        }
      } else {
        console.error('WebSocket not open, state:', websocketRef.current?.readyState)
        throw new Error('WebSocket connection lost')
      }

    } catch (error) {
      console.error('Error starting recording:', error)
      console.error('Error details:', error)
      setState(prev => ({ 
        ...prev, 
        error: `Could not access microphone: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isRecording: false 
      }))
    }
  }, [connect])

  const stopRecording = useCallback(() => {
    console.log('Stopping recording...')
    
    // Clean up Web Audio API
    if (audioStreamRef.current) {
      const audioRefs = (audioStreamRef.current as any)._audioRefs
      if (audioRefs) {
        console.log('Cleaning up Web Audio API')
        audioRefs.processor.disconnect()
        audioRefs.source.disconnect()
        audioRefs.audioContext.close()
      }
      
      // Stop audio stream
      audioStreamRef.current.getTracks().forEach(track => track.stop())
      audioStreamRef.current = null
    }

    // Tell server to stop transcription
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      websocketRef.current.send(JSON.stringify({
        type: 'stop_transcription'
      }))
    }

    setState(prev => ({ ...prev, isRecording: false }))
  }, [state.isRecording])

  const clearText = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      currentText: '', 
      finalText: '', 
      error: null 
    }))
  }, [])

  const disconnect = useCallback(() => {
    // Stop recording if active
    if (state.isRecording) {
      stopRecording()
    }

    // Close WebSocket
    if (websocketRef.current) {
      websocketRef.current.close()
      websocketRef.current = null
    }

    isConnectedRef.current = false
    setState({
      isConnecting: false,
      isRecording: false,
      isProcessing: false,
      currentText: '',
      finalText: '',
      error: null
    })
  }, [state.isRecording, stopRecording])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('useVoiceTranscription cleanup called')
      disconnect()
    }
  }, [])

  return {
    state,
    startRecording,
    stopRecording,
    clearText,
    disconnect
  }
}